/*
 * Shared product gallery behavior core.
 *
 * Use this file for logic that should stay identical across gallery layouts:
 * variant resolution, swatch/chip state, grouped media selection, price/form sync,
 * URL syncing, and shared lightbox behavior.
 *
 * Why this file exists:
 * - keeps gallery-group + variant logic in one place
 * - lets contained/editorial layouts focus only on presentation and interaction
 * - makes future gallery experiments cheaper because they reuse the same core
 */

(function () {
  if (window.ProductGalleryCore) return;

  class ProductGalleryCore extends HTMLElement {
    constructor() {
      super();

      this.variantData = JSON.parse(this.querySelector('[data-product-variants-json]')?.textContent || '[]');
      this.optionNames = JSON.parse(this.querySelector('[data-option-names-json]')?.textContent || '[]');
      this.galleryGroups = this.parseGalleryGroups(this.querySelector('[data-gallery-groups-json]')?.textContent || '[]');
      this.selectedOptions = [...this.optionNames];
      this.currentVariant = null;
      this.currentMediaIds = [];
      this.currentIndex = 0;
      this.scrollFrame = 0;
      this.colorOptionIndex = Number(this.dataset.colorOptionIndex || 0);

      this.lightboxOpen = false;
      this.lightboxDefaultScale = 1.65;
      this.lightboxMinScale = 1;
      this.lightboxMaxScale = 4;
      this.lightboxScale = this.lightboxDefaultScale;
      this.lightboxTranslate = { x: 0, y: 0 };
      this.lightboxGesturePointers = new Map();
      this.lightboxGestureStartDistance = 0;
      this.lightboxGestureStartScale = this.lightboxDefaultScale;
      this.lightboxGestureStartTranslate = { x: 0, y: 0 };
      this.lightboxGestureStartCenter = { x: 0, y: 0 };
      this.lightboxDragStart = { x: 0, y: 0 };
      this.lightboxDragOrigin = { x: 0, y: 0 };
      this.lightboxPointerId = null;
    }

    connectedCallback() {
      this.cacheSharedDom();
      this.cacheLayoutDom?.();
      this.bindSharedEvents();
      this.bindLayoutEvents?.();
      this.bootstrapState();
    }

    disconnectedCallback() {
      this.unbindSharedEvents();
      this.unbindLayoutEvents?.();
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      window.removeEventListener('popstate', this.handlePopState);
      document.removeEventListener('keydown', this.handleDocumentKeydown);
      document.documentElement.classList.remove('product-gallery-lightbox-open');
    }

    cacheSharedDom() {
      this.priceNode = this.querySelector('[data-gallery-price]');
      this.compareNode = this.querySelector('[data-gallery-compare-price]');
      this.variantIdInput = this.querySelector('input[name="id"]');
      this.addToCartButton = this.querySelector('[data-gallery-add-to-cart]');
      this.addToCartLabel = this.addToCartButton?.querySelector('span');
      this.debugNode = this.querySelector('[data-gallery-debug]');
      this.swatchGroups = [...this.querySelectorAll('[data-option-index]')];

      this.slideContainer = this.querySelector('[data-gallery-slides]');
      this.slides = [...this.querySelectorAll('[data-gallery-slide][data-media-id]')];
      this.allImageIds = this.slides.map((slide) => String(slide.dataset.mediaId));
      this.thumbContainer = this.querySelector('[data-gallery-thumbs]');
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb][data-media-id]')];
      this.pagination = this.querySelector('[data-gallery-pagination]');
      this.mainViewer = this.querySelector('[data-gallery-viewer]');

      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      this.lightboxSlidesContainer = this.querySelector('[data-gallery-lightbox-slides]');
      this.lightboxSlides = [...this.querySelectorAll('[data-gallery-lightbox-slide][data-media-id]')];
      this.lightboxStage = this.querySelector('[data-gallery-lightbox-stage]');
      this.lightboxCloseButtons = [...this.querySelectorAll('[data-gallery-close-lightbox]')];
    }

    bindSharedEvents() {
      this.swatchGroups.forEach((group) => {
        group.addEventListener('click', (event) => {
          const button = event.target.closest('[data-option-value]');
          if (!button || button.classList.contains('is-unavailable')) return;

          const optionIndex = Number(group.dataset.optionIndex);
          this.selectedOptions[optionIndex] = button.dataset.optionValue;
          this.refreshState({ changedIndex: optionIndex });
        });
      });

      this.handleThumbContainerClick = (event) => {
        const thumb = event.target.closest('[data-gallery-thumb][data-media-id]');
        if (!thumb) return;
        this.goToMediaId(String(thumb.dataset.mediaId));
      };

      this.handleSlideContainerClick = (event) => {
        const slide = event.target.closest('[data-gallery-slide][data-media-id]');
        if (!slide || slide.hidden) return;
        this.openLightboxForMediaId(String(slide.dataset.mediaId));
      };

      this.slideContainer?.addEventListener('click', this.handleSlideContainerClick);
      this.thumbContainer?.addEventListener('click', this.handleThumbContainerClick);

      this.lightboxCloseButtons.forEach((button) => {
        button.addEventListener('click', this.handleCloseLightboxClick);
      });

      this.lightboxStage?.addEventListener('pointerdown', this.handleLightboxPointerDown);
      this.lightboxStage?.addEventListener('pointermove', this.handleLightboxPointerMove);
      this.lightboxStage?.addEventListener('pointerup', this.handleLightboxPointerUp);
      this.lightboxStage?.addEventListener('pointercancel', this.handleLightboxPointerUp);

      window.addEventListener('popstate', this.handlePopState);
      document.addEventListener('keydown', this.handleDocumentKeydown);
    }

    unbindSharedEvents() {
      this.slideContainer?.removeEventListener('click', this.handleSlideContainerClick);
      this.thumbContainer?.removeEventListener('click', this.handleThumbContainerClick);

      this.lightboxCloseButtons.forEach((button) => {
        button.removeEventListener('click', this.handleCloseLightboxClick);
      });

      this.lightboxStage?.removeEventListener('pointerdown', this.handleLightboxPointerDown);
      this.lightboxStage?.removeEventListener('pointermove', this.handleLightboxPointerMove);
      this.lightboxStage?.removeEventListener('pointerup', this.handleLightboxPointerUp);
      this.lightboxStage?.removeEventListener('pointercancel', this.handleLightboxPointerUp);
    }

    parseGalleryGroups(rawValue) {
      try {
        const parsed = JSON.parse(rawValue || '[]');

        return (Array.isArray(parsed) ? parsed : []).map((group) => ({
          label: String(group.label || ''),
          optionValue: String(group.optionValue || group.label || ''),
          mediaIds: Array.isArray(group.mediaIds) ? group.mediaIds.map((mediaId) => String(mediaId)) : [],
        }));
      } catch (error) {
        console.warn('Failed to parse product gallery groups', error);
        return [];
      }
    }

    bootstrapState() {
      const urlVariantId = Number(new URL(window.location.href).searchParams.get('variant'));
      const initialVariantId = urlVariantId || Number(this.dataset.initialVariantId);
      const initialVariant = this.variantData.find((variant) => variant.id === initialVariantId) || this.variantData[0];
      if (!initialVariant) return;

      this.selectedOptions = [...initialVariant.options];
      this.refreshState({ preserveIndex: false });
    }

    refreshState({ changedIndex = 0, preserveIndex = false } = {}) {
      this.currentVariant = this.findVariantFromSelection() || this.findFirstAvailableVariant(changedIndex);
      if (!this.currentVariant) return;

      this.selectedOptions = [...this.currentVariant.options];

      this.paintOptionButtons();
      this.paintPrice();
      this.paintForm();
      this.paintGallery({ preserveIndex });
      this.paintDebug();
      this.syncUrl();
    }

    findVariantFromSelection() {
      return this.variantData.find((variant) =>
        variant.options.every((optionValue, index) => optionValue === this.selectedOptions[index])
      );
    }

    findFirstAvailableVariant(changedIndex = 0) {
      const leadingOptions = this.selectedOptions.slice(0, changedIndex + 1);
      const matchingVariants = this.variantData.filter((variant) =>
        leadingOptions.every((optionValue, index) => variant.options[index] === optionValue)
      );

      return matchingVariants.find((variant) => variant.available) || matchingVariants[0] || this.variantData[0];
    }

    paintOptionButtons() {
      this.swatchGroups.forEach((group) => {
        const optionIndex = Number(group.dataset.optionIndex);
        const buttons = [...group.querySelectorAll('[data-option-value]')];

        buttons.forEach((button) => {
          const optionValue = button.dataset.optionValue;
          const isSelected = this.selectedOptions[optionIndex] === optionValue;
          const isAvailable = this.variantData.some((variant) => {
            return variant.available && variant.options.every((variantOptionValue, variantOptionIndex) => {
              if (variantOptionIndex === optionIndex) return variantOptionValue === optionValue;
              if (variantOptionIndex < optionIndex) return variantOptionValue === this.selectedOptions[variantOptionIndex];
              return true;
            });
          });

          button.classList.toggle('is-active', isSelected);
          button.classList.toggle('is-unavailable', !isAvailable);
          button.setAttribute('aria-pressed', String(isSelected));
        });
      });
    }

    paintPrice() {
      if (!this.currentVariant) return;

      const formatMoney = (amount) => {
        if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(amount, window.money_format);
        return `$${(amount / 100).toFixed(2)}`;
      };

      if (this.priceNode) this.priceNode.textContent = formatMoney(this.currentVariant.price);

      if (this.compareNode) {
        if (this.currentVariant.compare_at_price && this.currentVariant.compare_at_price > this.currentVariant.price) {
          this.compareNode.hidden = false;
          this.compareNode.textContent = formatMoney(this.currentVariant.compare_at_price);
        } else {
          this.compareNode.hidden = true;
          this.compareNode.textContent = '';
        }
      }
    }

    paintForm() {
      if (this.variantIdInput) {
        this.variantIdInput.value = this.currentVariant.id;
        this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (this.addToCartButton) {
        this.addToCartButton.disabled = !this.currentVariant.available;
      }

      if (this.addToCartLabel) {
        this.addToCartLabel.textContent = this.currentVariant.available
          ? window.variantStrings?.addToCart || 'Add to cart'
          : window.variantStrings?.soldOut || 'Sold out';
      }
    }

    getSelectedColor() {
      return this.selectedOptions[this.colorOptionIndex] || this.selectedOptions[0] || '';
    }

    getAllImageIds() {
      return [...this.allImageIds];
    }

    getExplicitMediaIds(selectedColor) {
      const galleryGroupMatch = this.galleryGroups.find(
        (group) => group.optionValue === selectedColor || group.label === selectedColor
      );
      return galleryGroupMatch?.mediaIds?.length ? galleryGroupMatch.mediaIds : [];
    }

    getMediaIdsForVariant() {
      const selectedColor = this.getSelectedColor();
      const explicitMediaIds = this.getExplicitMediaIds(selectedColor);
      const existingIds = new Set(this.getAllImageIds());
      const filteredExplicitIds = explicitMediaIds.filter((mediaId, index) => {
        return existingIds.has(String(mediaId)) && explicitMediaIds.indexOf(mediaId) === index;
      });

      return filteredExplicitIds.length ? filteredExplicitIds : this.getAllImageIds();
    }

    getStartingIndex(mediaIds, preserveIndex) {
      if (!mediaIds.length) return 0;

      if (preserveIndex) {
        const currentMediaId = this.currentMediaIds[this.currentIndex];
        const preservedIndex = mediaIds.indexOf(currentMediaId);
        if (preservedIndex >= 0) return preservedIndex;
      }

      const featuredMediaId = String(this.currentVariant?.featured_media?.id || '');
      const featuredIndex = mediaIds.indexOf(featuredMediaId);
      if (featuredIndex >= 0) return featuredIndex;

      return 0;
    }

    paintGallery({ preserveIndex = false } = {}) {
      const mediaIds = this.getMediaIdsForVariant();
      this.currentMediaIds = mediaIds;

      this.reorderByMediaIds(this.slideContainer, this.slides, mediaIds);
      this.reorderByMediaIds(this.thumbContainer, this.thumbs, mediaIds);
      this.reorderByMediaIds(this.lightboxSlidesContainer, this.lightboxSlides, mediaIds);

      this.toggleMediaVisibility(this.slides, mediaIds);
      this.toggleMediaVisibility(this.thumbs, mediaIds);
      this.toggleMediaVisibility(this.lightboxSlides, mediaIds);

      this.currentIndex = this.getStartingIndex(mediaIds, preserveIndex);
      this.paintPagination();
      this.paintActiveMedia();
      this.syncViewerPosition({ behavior: 'auto' });
    }

    reorderByMediaIds(container, elements, mediaIds) {
      if (!container || !elements?.length) return;

      const itemsById = new Map(elements.map((element) => [String(element.dataset.mediaId), element]));
      mediaIds.forEach((mediaId) => {
        const element = itemsById.get(String(mediaId));
        if (element) container.appendChild(element);
      });
    }

    toggleMediaVisibility(elements, mediaIds) {
      if (!elements?.length) return;

      elements.forEach((element) => {
        const shouldShow = mediaIds.includes(String(element.dataset.mediaId));
        element.hidden = !shouldShow;
        element.setAttribute('aria-hidden', String(!shouldShow));
      });
    }

    paintPagination() {}

    paintActiveMedia() {
      const activeMediaId = this.currentMediaIds[this.currentIndex] || this.currentMediaIds[0];

      this.slides.forEach((slide) => {
        const isActive = String(slide.dataset.mediaId) === activeMediaId;
        slide.classList.toggle('is-active', isActive);
        slide.tabIndex = isActive ? 0 : -1;
        slide.setAttribute('aria-current', String(isActive));
      });

      this.thumbs.forEach((thumb) => {
        const isActive = String(thumb.dataset.mediaId) === activeMediaId;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', String(isActive));
      });

      [...this.querySelectorAll('[data-gallery-dot]')].forEach((dot) => {
        const isActive = String(dot.dataset.mediaId) === activeMediaId;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', String(isActive));
      });

      this.lightboxSlides.forEach((slide) => {
        const isActive = String(slide.dataset.mediaId) === activeMediaId;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-current', String(isActive));

        const image = slide.querySelector('img');
        if (image && !isActive) image.style.transform = '';
      });

      this.resetLightboxPan();
    }

    goToRelative(delta) {
      if (!this.currentMediaIds.length) return;
      const nextIndex = (this.currentIndex + delta + this.currentMediaIds.length) % this.currentMediaIds.length;
      this.goToIndex(nextIndex);
    }

    goToIndex(index, { behavior = 'smooth' } = {}) {
      if (!this.currentMediaIds.length) return;
      this.currentIndex = Math.max(0, Math.min(index, this.currentMediaIds.length - 1));
      this.paintActiveMedia();
      this.syncViewerPosition({ behavior });
    }

    goToMediaId(mediaId) {
      const nextIndex = this.currentMediaIds.indexOf(String(mediaId));
      if (nextIndex >= 0) this.goToIndex(nextIndex);
    }

    getSlideByMediaId(mediaId) {
      return this.slides.find((slide) => String(slide.dataset.mediaId) === String(mediaId));
    }

    getNearestIndexFromScroll() {
      if (!this.slideContainer || !this.currentMediaIds.length) return 0;

      let nearestIndex = 0;
      let smallestDistance = Number.POSITIVE_INFINITY;

      this.currentMediaIds.forEach((mediaId, index) => {
        const slide = this.getSlideByMediaId(mediaId);
        if (!slide || slide.hidden) return;

        const distance = Math.abs(slide.offsetLeft - this.slideContainer.scrollLeft);
        if (distance < smallestDistance) {
          smallestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    }

    syncViewerPosition() {}

    openLightboxForMediaId(mediaId) {
      if (!this.lightbox) return;

      const nextIndex = this.currentMediaIds.indexOf(String(mediaId));
      if (nextIndex < 0) return;

      this.goToIndex(nextIndex, { behavior: 'auto' });
      this.openLightbox();
    }

    openLightbox() {
      if (!this.lightbox) return;

      this.lightbox.hidden = false;
      this.lightboxOpen = true;
      document.documentElement.classList.add('product-gallery-lightbox-open');
      this.resetLightboxPan();
      this.lightboxStage?.focus();
    }

    closeLightbox() {
      if (!this.lightbox) return;

      this.lightbox.hidden = true;
      this.lightboxOpen = false;
      this.lightboxPointerId = null;
      this.lightboxGesturePointers.clear();
      this.lightboxStage?.classList.remove('is-dragging');
      document.documentElement.classList.remove('product-gallery-lightbox-open');
      this.resetLightboxPan();
      this.mainViewer?.focus?.();
    }

    getActiveLightboxImage() {
      const activeMediaId = this.currentMediaIds[this.currentIndex] || this.currentMediaIds[0];
      const activeSlide = this.lightboxSlides.find((slide) => String(slide.dataset.mediaId) === String(activeMediaId));
      return activeSlide?.querySelector('img') || null;
    }

    resetLightboxPan() {
      this.lightboxScale = this.lightboxDefaultScale;
      this.lightboxTranslate = { x: 0, y: 0 };
      this.lightboxGesturePointers.clear();
      this.applyLightboxTransform();
    }

    applyLightboxTransform() {
      const image = this.getActiveLightboxImage();
      if (!image) return;

      image.style.transform = `translate3d(${this.lightboxTranslate.x}px, ${this.lightboxTranslate.y}px, 0) scale(${this.lightboxScale})`;
    }

    getLightboxTranslateBounds() {
      if (!this.lightboxStage) return { maxX: 0, maxY: 0 };

      return {
        maxX: Math.max(0, (this.lightboxStage.clientWidth * (this.lightboxScale - 1)) / 2),
        maxY: Math.max(0, (this.lightboxStage.clientHeight * (this.lightboxScale - 1)) / 2),
      };
    }

    clampLightboxTranslate(nextTranslate) {
      const bounds = this.getLightboxTranslateBounds();

      return {
        x: Math.max(-bounds.maxX, Math.min(bounds.maxX, nextTranslate.x)),
        y: Math.max(-bounds.maxY, Math.min(bounds.maxY, nextTranslate.y)),
      };
    }

    clampLightboxScale(nextScale) {
      return Math.max(this.lightboxMinScale, Math.min(this.lightboxMaxScale, nextScale));
    }

    getPointerDistance(pointerA, pointerB) {
      return Math.hypot(pointerB.x - pointerA.x, pointerB.y - pointerA.y);
    }

    getPointerCenter(pointerA, pointerB) {
      return {
        x: (pointerA.x + pointerB.x) / 2,
        y: (pointerA.y + pointerB.y) / 2,
      };
    }

    updateLightboxPointer(event) {
      this.lightboxGesturePointers.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      });
    }

    handleCloseLightboxClick = () => {
      this.closeLightbox();
    };

    handleLightboxPointerDown = (event) => {
      if (!this.lightboxOpen || !this.lightboxStage) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.updateLightboxPointer(event);
      this.lightboxStage.setPointerCapture(event.pointerId);

      if (this.lightboxGesturePointers.size === 1) {
        this.lightboxPointerId = event.pointerId;
        this.lightboxDragOrigin = { x: event.clientX, y: event.clientY };
        this.lightboxDragStart = { ...this.lightboxTranslate };
        this.lightboxStage.classList.add('is-dragging');
      }

      if (this.lightboxGesturePointers.size === 2) {
        const [pointerA, pointerB] = [...this.lightboxGesturePointers.values()];
        this.lightboxPointerId = null;
        this.lightboxStage.classList.remove('is-dragging');
        this.lightboxGestureStartDistance = this.getPointerDistance(pointerA, pointerB);
        this.lightboxGestureStartScale = this.lightboxScale;
        this.lightboxGestureStartTranslate = { ...this.lightboxTranslate };
        this.lightboxGestureStartCenter = this.getPointerCenter(pointerA, pointerB);
      }

      event.preventDefault();
    };

    handleLightboxPointerMove = (event) => {
      if (!this.lightboxOpen) return;
      if (!this.lightboxGesturePointers.has(event.pointerId)) return;

      this.updateLightboxPointer(event);

      if (this.lightboxGesturePointers.size >= 2) {
        const [pointerA, pointerB] = [...this.lightboxGesturePointers.values()];
        const nextDistance = this.getPointerDistance(pointerA, pointerB);
        const nextCenter = this.getPointerCenter(pointerA, pointerB);
        const distanceRatio = this.lightboxGestureStartDistance ? nextDistance / this.lightboxGestureStartDistance : 1;

        this.lightboxScale = this.clampLightboxScale(this.lightboxGestureStartScale * distanceRatio);
        this.lightboxTranslate = this.clampLightboxTranslate({
          x: this.lightboxGestureStartTranslate.x + (nextCenter.x - this.lightboxGestureStartCenter.x),
          y: this.lightboxGestureStartTranslate.y + (nextCenter.y - this.lightboxGestureStartCenter.y),
        });
        this.applyLightboxTransform();
        return;
      }

      if (event.pointerId !== this.lightboxPointerId) return;

      const nextTranslate = {
        x: this.lightboxDragStart.x + (event.clientX - this.lightboxDragOrigin.x),
        y: this.lightboxDragStart.y + (event.clientY - this.lightboxDragOrigin.y),
      };

      this.lightboxTranslate = this.clampLightboxTranslate(nextTranslate);
      this.applyLightboxTransform();
    };

    handleLightboxPointerUp = (event) => {
      if (!this.lightboxStage || !this.lightboxGesturePointers.has(event.pointerId)) return;

      this.lightboxGesturePointers.delete(event.pointerId);

      if (this.lightboxStage.hasPointerCapture(event.pointerId)) {
        this.lightboxStage.releasePointerCapture(event.pointerId);
      }

      if (this.lightboxGesturePointers.size === 1) {
        const [remainingPointerId, remainingPointer] = [...this.lightboxGesturePointers.entries()][0];
        this.lightboxPointerId = remainingPointerId;
        this.lightboxDragOrigin = { x: remainingPointer.x, y: remainingPointer.y };
        this.lightboxDragStart = { ...this.lightboxTranslate };
        this.lightboxStage.classList.add('is-dragging');
        return;
      }

      this.lightboxPointerId = null;
      this.lightboxStage.classList.remove('is-dragging');
    };

    handleDocumentKeydown = (event) => {
      if (!this.lightboxOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeLightbox();
      }
    };

    paintDebug() {
      if (!this.debugNode || !this.currentVariant) return;
      const selectedColor = this.getSelectedColor();
      const galleryGroupMatch = this.galleryGroups.find(
        (group) => group.optionValue === selectedColor || group.label === selectedColor
      );
      const sourceLabel = galleryGroupMatch?.mediaIds?.length ? 'gallery-groups' : 'native-images';
      this.debugNode.textContent = `Variant ${this.currentVariant.id} · ${this.currentVariant.options.join(' / ')} · source: ${sourceLabel} · visible media: ${this.currentMediaIds.length}`;
    }

    syncUrl() {
      if (!this.currentVariant?.id) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(this.currentVariant.id));
      window.history.replaceState({}, '', url.toString());
    }

    handlePopState = () => {
      const urlVariantId = Number(new URL(window.location.href).searchParams.get('variant'));
      const variant = this.variantData.find((item) => item.id === urlVariantId);
      if (!variant) return;
      this.selectedOptions = [...variant.options];
      this.refreshState({ preserveIndex: false });
    };
  }

  window.ProductGalleryCore = ProductGalleryCore;
})();
